import { useRef, useState, useMemo } from 'react';
import { TimeSubmitIcon, TimeApprovedGreyedOutIcon, TimeSubmitGreyedOutIcon, TimeOpenIcon, TimeOpenGreyedOutIcon, TimeApprovedIcon } from '../Icons';
import {
    Stack,
    Label,
    TooltipHost,
    Text,
    FontIcon,
    IStackTokens,
    mergeStyleSets,
    TextField,
    PrimaryButton,
    DefaultButton,
    Callout,
    FontWeights,
    DirectionalHint,
    Dialog,
    DialogType,
    DialogFooter,
    makeStyles,
    StackItem,
    Icon,
} from '@fluentui/react';
import '../Styles/TimeStatePicker.scss';
import { useConfigValuesState } from '../hooks/useConfigValue';
import { Allocation, UserRegistrationPeriod, UserReportingPeriodState } from '../api/generated/data-contracts';
import { useTranslation } from 'react-i18next';
import { ListMissingAllocations } from './ListMissingAllocations';

interface TimeStatePickerProps {
    state: UserReportingPeriodState;
    stateChanged: (state: UserReportingPeriodState, comment?: string) => void;
    periodIsClosed: boolean;
    comment: string;
    submitComment: string;
    mytimeprojects: UserRegistrationPeriod[];
    allocations: Allocation[];
}

function TimeStatePicker(props: TimeStatePickerProps) {
    const { t } = useTranslation('timesheet');
    const styles = getStyles();

    const [showdetailsflyout, setShowdetailsflyout] = useState<boolean>(false);
    const [reopenDialogHidden, setReopenDialogHidden] = useState<boolean>(true);
    const [submitDialogHidden, setSubmitDialogHidden] = useState<boolean>(true);
    const [submitComment, setSubmitComment] = useState<string>(props.submitComment);

    const approveIconRef = useRef(null);

    const { mytimeprojects: projects } = props;

    const {
        initialData: {
            configuration: {
                allowTimeSheetComment,
                showAllocations,
                timeSheetValidation: { emptyTimeSheetAllowed },
            },
            aggregatedUser: { user },
        },
    } = useConfigValuesState();

    // TODO: get from config
    // const emptyTimeSheetNotAllowed = useMemo(() => uiCtx.enabledRules.find(_ => _.name === 'EmptyTimeSheetNotAllowed')?.enabled ?? false, [props]);
    // const emptyTimeSheetNotAllowed = false;
    const anyTimeEntries = useMemo(() => props.mytimeprojects.flatMap(_ => _.timeEntries).some(_ => _.hours > 0), [props.mytimeprojects]);

    const openclicked = (): void => {
        setReopenDialogHidden(false);
    };
    const reopenperiod = (): void => {
        props.stateChanged('Open');
    };

    const submitclicked = (): void => {
        setSubmitDialogHidden(false);
    };
    const executeSubmit = (): void => {
        props.stateChanged('Submitted', submitComment);
    };

    const onClose = () => {
        setSubmitDialogHidden(true);
        setSubmitComment('');
    };

    return (
        <Stack horizontal tokens={{ childrenGap: 16 }} verticalAlign="center">
            <Stack tokens={{ childrenGap: 2 }}>
                {!props.periodIsClosed && props.state === 'Open' && (
                    <div className="buttoncontainerstyle timestateenabled">
                        {/* <Timeopen /> */}
                        <div style={{ flexGrow: 1 }}>
                            <TimeOpenIcon />
                        </div>
                        <Text className="timestatelabel">{t('cta.action.open')}</Text>
                    </div>
                )}
                {!props.periodIsClosed && props.state === 'Rejected' && (
                    <div className="buttoncontainerstyle timestateenabled" onClick={reopenperiod}>
                        {/* <Timeopen /> */}
                        <div style={{ flexGrow: 1 }}>
                            <TimeOpenGreyedOutIcon />
                        </div>
                        <Text className="timestatelabel">{t('cta.action.open')}</Text>
                    </div>
                )}
                {!props.periodIsClosed && (props.state === 'Submitted' || props.state === 'Approved') && (
                    <div className="buttoncontainerstyle timestateenabled" onClick={openclicked}>
                        {/* <Timeopengray onClick={openclicked} /> */}
                        <div style={{ flexGrow: 1 }}>
                            <TimeOpenGreyedOutIcon />
                        </div>
                        <Text className="timestatelabel">{t('cta.action.open')}</Text>
                    </div>
                )}
                {props.periodIsClosed && (
                    <div className="buttoncontainerstyle timestatedisabled">
                        {/* <Timeopengray /> */}
                        <div style={{ flexGrow: 1 }}>
                            <TimeOpenGreyedOutIcon />
                        </div>
                        <Text className="timestatelabel">{t('cta.action.open')}</Text>
                    </div>
                )}
            </Stack>
            <Stack>
                {!props.periodIsClosed && (props.state === 'Submitted' || props.state === 'Approved') && (
                    <div id={`tft-submit`} className="buttoncontainerstyle timestateenabled">
                        {/* <Timesubmit /> */}
                        <div style={{ flexGrow: 1 }}>
                            <TimeSubmitIcon />
                        </div>
                        <Text className="timestatelabel">{t('cta.action.submitted')}</Text>
                    </div>
                )}
                {!props.periodIsClosed && (props.state === 'Open' || props.state === 'Rejected') && (
                    <div id={`tft-submit`} className="buttoncontainerstyle timestateenabled" onClick={submitclicked}>
                        {/* <Timesubmitgray onClick={submitclicked} /> */}
                        <div style={{ flexGrow: 1 }}>
                            <TimeSubmitGreyedOutIcon />
                        </div>
                        <Text className="timestatelabel">{t('cta.action.submit')}</Text>
                    </div>
                )}
                {props.periodIsClosed && (
                    <div id={`tft-submit`} className="buttoncontainerstyle timestatedisabled">
                        <div style={{ flexGrow: 1 }}>
                            <TimeSubmitIcon />
                        </div>
                        <Text className="timestatelabel">{t('cta.action.submit')}</Text>
                    </div>
                )}
            </Stack>
            <Stack>
                <div
                    ref={approveIconRef}
                    onClick={() => {
                        if (props.comment !== '') {
                            //setShowdetailsflyout(true)
                        }
                    }}
                >
                    {props.periodIsClosed ? (
                        props.state === 'Approved' ? (
                            <div id={`tft-approve`} className="buttoncontainerstyle timestatedisabled">
                                {/* <Timeapproved /> */}
                                <div style={{ flexGrow: 1 }}>
                                    <TimeApprovedIcon />
                                </div>
                                <Text className="timestatelabel">{t('cta.action.approved')}</Text>
                            </div>
                        ) : (
                            <div id={`tft-approve`} className="buttoncontainerstyle timestatedisabled">
                                <TooltipHost
                                    content={user.autoApprove ? t('cta.action.tooltip.approve.auto') : t('cta.action.tooltip.approve.manager')}
                                    closeDelay={500}
                                >
                                    {/* <Timeapprovedgray /> */}
                                    <div style={{ flexGrow: 1 }}>
                                        <TimeApprovedGreyedOutIcon />
                                    </div>
                                    <Text className="timestatelabel">{t('cta.action.approve')}</Text>
                                </TooltipHost>
                            </div>
                        )
                    ) : props.state === 'Approved' ? (
                        <div id={`tft-approve`} className="buttoncontainerstyle timestatedisabled">
                            {/* <Timeapproved /> */}
                            <div style={{ flexGrow: 1 }}>
                                <TimeApprovedIcon />
                            </div>
                            <Text className="timestatelabel">{t('cta.action.approved')}</Text>
                        </div>
                    ) : (
                        <div id={`tft-approve`} className="buttoncontainerstyle timestatedisabled">
                            <TooltipHost
                                content={user.autoApprove ? t('cta.action.tooltip.approve.auto') : t('cta.action.tooltip.approve.manager')}
                                closeDelay={500}
                            >
                                {/* <Timeapprovedgray /> */}
                                <div style={{ flexGrow: 1 }}>
                                    <TimeApprovedGreyedOutIcon />
                                </div>
                                <Text className="timestatelabel">{t('cta.action.approve')}</Text>
                            </TooltipHost>
                        </div>
                    )}
                </div>
                {showdetailsflyout && (
                    <Callout
                        directionalHint={DirectionalHint.bottomAutoEdge}
                        directionalHintFixed={true}
                        className={styles.callout}
                        role="alertdialog"
                        gapSpace={0}
                        target={approveIconRef}
                        onDismiss={() => {
                            setShowdetailsflyout(false);
                        }}
                    >
                        <div className={styles.header}>
                            <Text className={styles.title}>{t('approve.comment')}</Text>
                        </div>
                        <div className={styles.inner}>
                            <Text>{props.comment}</Text>
                        </div>
                    </Callout>
                )}
                <Dialog
                    hidden={reopenDialogHidden}
                    onDismiss={() => {
                        setReopenDialogHidden(true);
                    }}
                    dialogContentProps={{
                        type: DialogType.normal,
                        title: t('submit.dialog.title'),
                        closeButtonAriaLabel: 'Close',
                        subText: props.state === 'Approved' ? t('submit.dialog.subtext.approved') : t('submit.dialog.subtext.default'),
                    }}
                >
                    <DialogFooter>
                        <PrimaryButton
                            onClick={() => {
                                setReopenDialogHidden(true);
                                reopenperiod();
                            }}
                            text={t('submit.dialog.confirm')}
                        />
                        <DefaultButton
                            onClick={() => {
                                setReopenDialogHidden(true);
                            }}
                            text={t('submit.dialog.cancel')}
                        />
                    </DialogFooter>
                </Dialog>
                <Dialog
                    hidden={submitDialogHidden}
                    onDismiss={() => {
                        setSubmitDialogHidden(true);
                    }}
                    minWidth={500}
                    dialogContentProps={{
                        type: DialogType.normal,
                        title: t('submit.dialog.confirmation.title'),
                        closeButtonAriaLabel: t('submit.dialog.confirmation.close'),
                    }}
                >
                    <Label>{t('submit.dialog.confirmation.text')}</Label>
                    {!anyTimeEntries && !emptyTimeSheetAllowed && (
                        <Stack tokens={verticalGapStackTokens}>
                            <Stack horizontal verticalAlign="center" className={warningStyles.root}>
                                <FontIcon aria-label="Warning" iconName="Warning" className={warningStyles.icon} />
                                <Label className={warningStyles.label}>{t('submit.dialog.confirmation.warning.title')}</Label>
                            </Stack>
                            <Label>{t('submit.dialog.confirmation.warning.text')}</Label>
                            <Label>{t('submit.dialog.confirmation.warning.confirm')}</Label>
                        </Stack>
                    )}
                    {showAllocations && <ListMissingAllocations projects={projects} allocations={props.allocations} />}
                    {/* If time sheet comment is allowed, show text field */}
                    {allowTimeSheetComment && (
                        <Stack horizontal>
                            <StackItem grow>
                                <TextField
                                    value={submitComment}
                                    onChange={(e, n) => {
                                        setSubmitComment(n);
                                    }}
                                    multiline
                                    autoAdjustHeight
                                />
                            </StackItem>
                            <StackItem>
                                <Icon style={{ padding: 5 }} onClick={() => setSubmitComment('')} iconName="Clear" />
                            </StackItem>
                        </Stack>
                    )}
                    <DialogFooter>
                        <PrimaryButton
                            onClick={() => {
                                onClose();
                                executeSubmit();
                            }}
                            text={t('submit.dialog.confirmation.submit')}
                        />
                        <DefaultButton onClick={onClose} text={t('submit.dialog.confirmation.cancel')} />
                    </DialogFooter>
                </Dialog>
            </Stack>
        </Stack>
    );
}

export default TimeStatePicker;

const warningStyles = mergeStyleSets({
    root: {
        fontSize: '20px',
    },
    icon: {
        color: 'red',
    },
    label: {
        marginLeft: '5px !important',
    },
});

const verticalGapStackTokens: IStackTokens = {
    childrenGap: 10,
    padding: 10,
};

const getStyles = makeStyles(theme => ({
    callout: {
        maxWidth: 800,
        minWidth: 300,
    },
    header: {
        padding: '18px 24px 12px',
    },
    title: [
        theme.fonts.mediumPlus,
        {
            margin: 0,
            fontWeight: FontWeights.semilight,
        },
    ],
    inner: {
        height: '100%',
        padding: '0 24px 20px',
    },
}));
